import Config from 'config/settings';
import { globals } from 'config/globals';
import HomeColours from 'app/views/home/config/HomeColours';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import HomeZonesConfig from 'app/views/home/config/HomeZonesConfig';
import ParticleGridPatterns from 'app/views/home/webgl/particles/targets/ParticleGridPatterns';
import ParticleTargets from 'app/views/home/targets/ParticleTargets';
import { device } from 'utils/device';

/**
 * The systems dot-grid's tunable values — every member defaults to the
 * legacy inline constant it replaced (SYSTEMS_GRID_DEFAULTS below), so a
 * caller that never calls configure() gets today's grid bit-for-bit. The
 * 'particles' reference type never configures; the 'particles-optimised'
 * department owns a set-point copy (departments/systems/systemsGridConfig.ts).
 *
 * REGENERATION SEMANTICS (which fields take effect when):
 *  - createGrid-scope (need a grid rebuild): baseBlockSizePx, the min-scale /
 *    scale-reference / grid-dimension members, the deadPixelSeed* rolls,
 *    edgeBand*, pointSizeMax, pointColours. configure() invalidates like
 *    resize() does (created = false), so the next tick() rebuilds.
 *  - drawGuide-scope (need the drawn-once guide Graphics redrawn): guideAlpha,
 *    guideDotScaleMobile, safeZoneDotChance/Colour, sparsePadding*, extraDot*.
 *    configure() clears guideDrawn, so the next tick() redraws.
 *  - per-tick (apply on the very next tick, no invalidation needed):
 *    portraitCenterYFactor, safeZoneWidthFactor/HeightFactor, safeZoneInsetXPx,
 *    guideFadeStartProgress, guideMax/MinAlpha(±Mobile).
 */
export interface SystemsGridConfig {
	//-- createGrid-scope ------------------------------------------------------
	/** Cell size in px at the 1920w reference scale (blockSize = round(this * scale)). */
	baseBlockSizePx: number;
	/** Scale floor on landscape layouts. */
	landscapeMinGridScale: number;
	/** Scale floor on portrait layouts. */
	portraitMinGridScale: number;
	/** Viewport width at which the grid renders 1:1 on landscape (scale = innerWidth / this). */
	landscapeScaleReferenceWidthPx: number;
	/** Portrait equivalent of the scale reference width. */
	portraitScaleReferenceWidthPx: number;
	/** Portrait grid height as a fraction of innerHeight. */
	portraitGridHeightFactor: number;
	/** Landscape grid width factor when innerWidth <= HomeZonesConfig.noOverlapSize. */
	landscapeGridWidthFactorNarrow: number;
	/** Landscape grid width factor on wider viewports. */
	landscapeGridWidthFactorWide: number;
	/** Landscape grid height = innerHeight - this. */
	landscapeGridHeightInsetPx: number;
	/** Chance a cell is dead (undrawn) — landscape interior. */
	deadPixelSeedLandscape: number;
	/** Chance a cell is dead — portrait (interior AND edge band). */
	deadPixelSeedPortrait: number;
	/** Chance a cell is dead in the landscape edge band (sparser border). */
	deadPixelSeedEdgeLandscape: number;
	/** Edge-band width in columns (each side) for the edge seed. */
	edgeBandCols: number;
	/** Edge-band height in rows (each side) for the edge seed. */
	edgeBandRows: number;
	/** Dot size roll: Math.ceil(random * this) px. */
	pointSizeMax: number;
	/** Palette the live dots pick from. */
	pointColours: Array<string>;
	//-- per-tick --------------------------------------------------------------
	/** Portrait grid centre Y as a fraction of innerHeight. */
	portraitCenterYFactor: number;
	/** Safe-zone (item-clearing hole) width as a fraction of the tracked sprite width. */
	safeZoneWidthFactor: number;
	/** Safe-zone height as a fraction of the tracked sprite height. */
	safeZoneHeightFactor: number;
	/** X nudge (px) of the safe zone's left edge. */
	safeZoneInsetXPx: number;
	/** Drag progress at which the guide starts fading up to guideMaxAlpha. */
	guideFadeStartProgress: number;
	/** Guide container alpha ceiling (desktop / mobile). */
	guideMaxAlpha: number;
	guideMaxAlphaMobile: number;
	/** Guide container alpha floor (desktop / mobile). */
	guideMinAlpha: number;
	guideMinAlphaMobile: number;
	//-- drawGuide-scope -------------------------------------------------------
	/** Alpha of the guide Graphics itself (multiplies the container fade). */
	guideAlpha: number;
	/** Dot scale multiplier below Config.mobileBreakpoint. */
	guideDotScaleMobile: number;
	/** Chance a safe-zone cell still draws a (dim) dot. */
	safeZoneDotChance: number;
	/** Colour of those dim safe-zone dots. */
	safeZoneDotColour: string;
	/** Height (px) of the sparse dot band above the grid. */
	sparsePaddingTopPx: number;
	/** Height (px) of the sparse dot band below the grid (desktop). */
	sparsePaddingBottomPx: number;
	/** Mobile bottom band height as a fraction of innerHeight. */
	sparsePaddingBottomMobileHeightFactor: number;
	/** Sparse-band dot size roll: Math.ceil(random * this) * scale px. */
	extraDotSizeMax: number;
	/** Chance a sparse-band cell draws a dot. */
	extraDotDensity: number;
	/** Chance a sparse dot is coloured (else extraDotBaseColour). */
	extraDotColourChance: number;
	/** Palette the coloured sparse dots pick from. */
	extraDotColours: Array<string>;
	/** Fallback sparse dot colour. */
	extraDotBaseColour: string;
}

/** Defaults == the legacy inline constants, verbatim — the reference
 *  'particles' type runs on exactly these (it never calls configure). */
export const SYSTEMS_GRID_DEFAULTS: SystemsGridConfig = {
	baseBlockSizePx: 30,
	landscapeMinGridScale: 0.5,
	portraitMinGridScale: 0.15,
	landscapeScaleReferenceWidthPx: 1920,
	portraitScaleReferenceWidthPx: 1300,
	portraitGridHeightFactor: 0.5,
	landscapeGridWidthFactorNarrow: 0.9,
	landscapeGridWidthFactorWide: 0.8,
	landscapeGridHeightInsetPx: 175,
	deadPixelSeedLandscape: 0.5,
	deadPixelSeedPortrait: 0.55,
	deadPixelSeedEdgeLandscape: 0.75,
	edgeBandCols: 5,
	edgeBandRows: 4,
	pointSizeMax: 1.99,
	pointColours: HomeColours.greys,
	portraitCenterYFactor: 0.35,
	safeZoneWidthFactor: 0.95,
	safeZoneHeightFactor: 0.52,
	safeZoneInsetXPx: 10,
	guideFadeStartProgress: 0.7,
	guideMaxAlpha: 0.85,
	guideMaxAlphaMobile: 0.48,
	guideMinAlpha: 0.55,
	guideMinAlphaMobile: 0.35,
	guideAlpha: 0.6,
	guideDotScaleMobile: 0.7,
	safeZoneDotChance: 0.4,
	safeZoneDotColour: '#666666',
	sparsePaddingTopPx: 100,
	sparsePaddingBottomPx: 100,
	sparsePaddingBottomMobileHeightFactor: 0.4,
	extraDotSizeMax: 1.99,
	extraDotDensity: 0.2,
	extraDotColourChance: 0.75,
	extraDotColours: HomeColours.blues,
	extraDotBaseColour: '#ffffff'
};

export default {
	cols: null,
	rows: null,
	deadpixels: [],
	pointConfig: [],
	activePoints: [],
	//Per-tick reuse caches, rebuilt in createGrid() (i.e. on resize/first
	//tick), NOT per tick. Keys and target arrays depend only on col/row:
	targetKeys: [], //targetKeys[c][r] = 'grid_c_r'
	targetPoints: [], //targetPoints[c][r] = reusable [x, y]
	blockSize: 10,
	drawGuides: true,
	//Drawn-once guard for drawGuide(). Was `guideContainer.cacheAsBitmap` — a
	//plain expando (inert) on Pixi 8.0-8.5, but the 8.6+ deprecation shim turns
	//that property into a REAL render-texture cache with unintended side
	//effects; a plain field keeps the original flag-only semantics.
	guideDrawn: false,
	guideContainer: null,
	guide: null,
	created: false,
	innerHeight: null,
	innerWidth: null,
	safeZoneWidth: null,
	safeZoneHeight: null,
	gridCenterX: null,
	gridCenterY: null,
	//Active knob set — SYSTEMS_GRID_DEFAULTS until configure() is called:
	config: SYSTEMS_GRID_DEFAULTS,

	setGuideContainer(c) {
		this.guideContainer = c;
	},

	/**
	 * Optional department set-point: merge `config` over SYSTEMS_GRID_DEFAULTS
	 * and invalidate exactly like resize() (created/guideDrawn cleared), so
	 * createGrid- and drawGuide-scope values regenerate on the next tick();
	 * per-tick values apply immediately — see SystemsGridConfig's header for
	 * the field-by-field split. Never called by the reference 'particles'
	 * type: its grid runs on the untouched defaults (== the legacy constants).
	 */
	configure(config: Partial<SystemsGridConfig>) {
		this.config = { ...SYSTEMS_GRID_DEFAULTS, ...config };
		this.created = false;
		this.guideDrawn = false;
	},

	createGrid() {
		if (!this.innerHeight) {
			this.innerHeight = window.innerHeight;
			this.innerWidth = window.innerWidth;
		}

		this.layout = this.innerWidth <= HomeScreens.switchScreensLayoutAt ? 'portrait' : 'landscape';

		const cfg: SystemsGridConfig = this.config;

		let scale;
		let baseBlockSize = cfg.baseBlockSizePx;
		let minGridScale = cfg.landscapeMinGridScale;

		if (this.innerWidth <= HomeScreens.switchScreensLayoutAt) {
			minGridScale = cfg.portraitMinGridScale;
			this.gridWidth = this.innerWidth;
			this.gridHeight = this.innerHeight * cfg.portraitGridHeightFactor;
			scale = Math.max(this.innerWidth / cfg.portraitScaleReferenceWidthPx, minGridScale);
		} else {
			this.gridWidth =
				this.innerWidth <= HomeZonesConfig.noOverlapSize
					? this.innerWidth * cfg.landscapeGridWidthFactorNarrow
					: this.innerWidth * cfg.landscapeGridWidthFactorWide;
			this.gridHeight = this.innerHeight - cfg.landscapeGridHeightInsetPx;

			scale = Math.max(this.innerWidth / cfg.landscapeScaleReferenceWidthPx, minGridScale);
		}

		//(Pre-existing name quirk kept: `mobileLayout` is true on WIDE screens.)
		const mobileLayout = device.innerWidth > HomeScreens.switchScreensLayoutAt;
		let deadPixelSeed = mobileLayout ? cfg.deadPixelSeedLandscape : cfg.deadPixelSeedPortrait;
		let deadPixelSeedEdge = mobileLayout ? cfg.deadPixelSeedEdgeLandscape : deadPixelSeed;
		let useDeadPixelSeed = deadPixelSeed;
		this.blockSize = Math.round(baseBlockSize * scale);

		this.deadpixels = [];
		this.cols = Math.ceil(this.gridWidth / this.blockSize);
		this.rows = Math.ceil(this.gridHeight / this.blockSize);
		let isDeadPixel = false;

		for (var c = 1; c <= this.cols; c++) {
			for (var r = 1; r <= this.rows; r++) {
				useDeadPixelSeed =
					c < cfg.edgeBandCols || this.cols - c <= cfg.edgeBandCols || r < cfg.edgeBandRows || this.rows - r <= cfg.edgeBandRows
						? deadPixelSeedEdge
						: deadPixelSeed;

				isDeadPixel = Math.random() < useDeadPixelSeed;

				if (isDeadPixel) {
					if (!this.deadpixels[c]) this.deadpixels[c] = [];
					this.deadpixels[c][r] = true;
				} else {
					if (!this.pointConfig[c]) this.pointConfig[c] = [];
					this.pointConfig[c][r] = {
						size: Math.ceil(Math.random() * cfg.pointSizeMax),
						colour: HomeColours.getRandomColour(cfg.pointColours)
					};
				}
			}
		}

		//Regeneration-time caches so tick() allocates nothing: prebuilt
		//'grid_c_r' key strings and one reusable [x, y] array per cell.
		this.targetKeys = [];
		this.targetPoints = [];
		this.activePoints = [];

		for (c = 1; c <= this.cols; c++) {
			this.targetKeys[c] = [];
			this.targetPoints[c] = [];

			for (r = 1; r <= this.rows; r++) {
				this.targetKeys[c][r] = 'grid_' + c + '_' + r;
				this.targetPoints[c][r] = [0, 0];
			}
		}

		this.created = true;

		ParticleGridPatterns.totalCols = this.cols;
		ParticleGridPatterns.totalRows = this.rows;
		ParticleGridPatterns.layout = this.layout;
	},

	tick(gridCenterX, gridCenterY, safeZoneWidth, safeZoneHeight, progress) {
		const cfg: SystemsGridConfig = this.config;

		this.gridCenterX = gridCenterX;
		this.gridCenterY =
			this.innerWidth <= HomeScreens.switchScreensLayoutAt ? this.innerHeight * cfg.portraitCenterYFactor : gridCenterY;

		let startX = Math.round(this.gridCenterX - this.gridWidth / 2);
		let startY = Math.round(this.gridCenterY - this.gridHeight / 2);

		this.startY = startY;

		if (!this.created) {
			this.createGrid();
		}

		//Clear last tick's active points in place — column arrays are reused
		//across ticks (consumers only read via ParticleGridPatterns.hasPoint,
		//nothing retains row references), so no per-tick reallocation:
		for (var c = 1; c < this.activePoints.length; c++) {
			if (this.activePoints[c]) this.activePoints[c].length = 0;
		}

		this.safeZoneWidth = safeZoneWidth * cfg.safeZoneWidthFactor;
		this.safeZoneHeight = safeZoneHeight * cfg.safeZoneHeightFactor;

		let x = startX;
		let y = startY;

		let blockWidth = this.blockSize;
		let blockHeight = this.blockSize;

		for (c = 1; c <= this.cols; c++) {
			let deadCol = this.deadpixels[c];
			let keyCol = this.targetKeys[c];
			let pointCol = this.targetPoints[c];
			let activeCol = this.activePoints[c];

			for (var r = 1; r <= this.rows; r++) {
				let isNotDeadPixel = !deadCol || !deadCol[r];
				if (!this.inSafeZone(x, y - startY) && isNotDeadPixel) {
					if (!activeCol) activeCol = this.activePoints[c] = [];
					activeCol[r] = r;

					//Reuse the cell's [x, y] array and prebuilt key — the
					//old path allocated a fresh array + key string per cell
					//per tick (profiled at 3.8% of busy time at idle):
					let point = pointCol[r];
					point[0] = Math.round(x);
					point[1] = Math.round(y);
					ParticleTargets.set(keyCol[r], point);
				}

				y += blockHeight;
			}

			y = startY;
			x += blockWidth;
		}

		ParticleGridPatterns.pointConfig = this.activePoints;

		let fadeAt = cfg.guideFadeStartProgress;
		let range = 1 - fadeAt;
		let ratio = (progress - fadeAt) / range;
		let maxAlpha = device.innerWidth < Config.mobileBreakpoint ? cfg.guideMaxAlphaMobile : cfg.guideMaxAlpha;
		let minAlpha = device.innerWidth < Config.mobileBreakpoint ? cfg.guideMinAlphaMobile : cfg.guideMinAlpha;

		this.guideContainer.alpha = Math.max(minAlpha, ratio * maxAlpha);


		if (this.drawGuides) {
			this.drawGuide(startX, startY);
		}
	},

	resize(innerWidth, innerHeight) {
		this.innerWidth = innerWidth;
		this.innerHeight = innerHeight;
		this.created = false;
		this.guideDrawn = false;
	},

	drawExtraDots(x, fromY, height, direction = -1, scale = 1) {
		const cfg: SystemsGridConfig = this.config;

		let size = Math.ceil(Math.random() * cfg.extraDotSizeMax) * scale;

		let rows = Math.ceil(height / this.blockSize);
		let sparseY = fromY + this.blockSize * direction;
		let sparseX = x;

		for (var i = 1; i <= this.cols; i++) {
			for (var j = 1; j <= rows; j++) {
				if (Math.random() < cfg.extraDotDensity) {
					let colour = cfg.extraDotBaseColour;
					if (Math.random() < cfg.extraDotColourChance) {
						colour = HomeColours.getRandomColour(cfg.extraDotColours);
					}

					this.guide.beginFill(colour);
					this.guide.drawRect(sparseX, sparseY, size, size);
					this.guide.endFill();
				}

				sparseY += this.blockSize * direction;
			}

			sparseY = fromY + this.blockSize * direction;
			sparseX += this.blockSize;
		}
	},

	drawGuide(xGrid, yGrid) {
		const cfg: SystemsGridConfig = this.config;

		this.guideContainer.x = xGrid;
		this.guideContainer.y = globals.yGrid = yGrid;

		let scale = device.innerWidth < Config.mobileBreakpoint ? cfg.guideDotScaleMobile : 1;

		if (this.guideDrawn) {
			return;
		}

		if (!this.guide) {
			this.guide = new PIXI.Graphics();
			this.guideContainer.addChild(this.guide);
		}

		this.guide.alpha = cfg.guideAlpha;

		this.guide.clear();

		let x = 0;
		let y = 0;

		//Draw top dots up
		let sparsePaddingTopPx = cfg.sparsePaddingTopPx;
		this.drawExtraDots(x, y, sparsePaddingTopPx, -1, scale);

		for (var i = 1; i <= this.cols; i++) {
			for (var j = 1; j <= this.rows; j++) {
				//Fallback size/colour NOT in SystemsGridConfig on purpose: a cell
				//without pointConfig is a dead pixel, which either skips drawing
				//or takes the safe-zone branch's own colour — this colour never
				//actually renders (the size can, in the safe-zone branch):
				let size = 1;
				let colour = '#333366';
				if (this.pointConfig[i] && this.pointConfig[i][j]) {
					size = this.pointConfig[i][j].size * scale;
					colour = this.pointConfig[i][j].colour;
				}

				let isNotDeadPixel = !this.deadpixels[i] || !this.deadpixels[i][j];
				let inSafeZone = this.inSafeZone(x, y, true);
				if (!inSafeZone && isNotDeadPixel) {
					this.guide.beginFill(colour);
					this.guide.drawRect(x, y, size, size);
					this.guide.endFill();
				} else if (inSafeZone) {
					if (Math.random() < cfg.safeZoneDotChance) {
						this.guide.beginFill(cfg.safeZoneDotColour);
						this.guide.drawRect(x, y, size, size);
						this.guide.endFill();
					}
				}

				y += this.blockSize;
			}

			y = 0;
			x += this.blockSize;
		}

		x = 0;
		let sparsePaddingBottomPx =
			device.innerWidth < Config.mobileBreakpoint
				? device.innerHeight * cfg.sparsePaddingBottomMobileHeightFactor
				: cfg.sparsePaddingBottomPx;
		this.drawExtraDots(x, (this.rows - 1) * this.blockSize, sparsePaddingBottomPx, 1, scale);

		this.guideDrawn = true;

	},

	inSafeZone(pointX, pointY, translatedCheck = false) {
		if (this.layout == 'portrait') return false;

		const centerX = translatedCheck ? this.gridWidth / 2 : this.gridCenterX;
		let x1 = Math.round(centerX - this.safeZoneWidth / 2) + this.config.safeZoneInsetXPx;
		let y1 = Math.round(this.gridCenterY - this.safeZoneHeight / 2) - this.startY;
		let x2 = x1 + this.safeZoneWidth;
		let y2 = y1 + this.safeZoneHeight;


		return pointX > x1 && pointX < x2 && pointY > y1 && pointY < y2;
	}
};
